import axios from 'axios';
import {
  User,
  Material,
  Resin,
  Consumption,
  Inventory,
  AuthResponse,
  Receipt,
  Disposal,
  ProjectFolder,
  Project,
  Task,
  Comment,
  ITTicket,
  ITTicketStatus,
  ITTicketProblemType,
  Announcement,
  FinanceCompany,
  FinanceDocumentType,
  FinanceBook,
  FinanceDocument,
  Worker,
  Position,
  AttendanceRecord,
  SickLeaveHistory,
  AnnualLeaveHistory,
  PaidUnpaidLeaveHistory,
  Absence,
  AbsenceType,
  ProcurementRequest,
  ProcurementStatus,
  ProcurementItem,
  LogisticsCategory,
  LogisticsGood,
  MagacinProduct,
  MagacinProductType,
  MagacinArrival,
  MagacinRemoval,
  MagacinLoan,
  MagacinReceiving,
  MagacinReceivingIssue,
  MagacinReceivingStatus,
  LagerCompany,
  LagerFinishedProduct,
  LagerTransport,
  LagerProductionRecord,
  HiltiWorker,
  HiltiProduct,
  HiltiMaterial,
  HiltiMaterialReceipt,
  HiltiMaterialDisposal,
  HiltiProductionRecord,
  HiltiDailyWorkRecord,
  Equipment,
  EquipmentCategory,
  Server,
  Computer,
  ComputerStatus,
  NetworkDevice,
  Vlan,
  ITIncident,
  ITIncidentCategory,
  ITIncidentPriority,
  ITIncidentStatus,
  ActivityLog,
  ChatRoom,
  ChatMessage,
  UserStatus
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: async (username: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  register: async (userData: Partial<User> & { password: string }): Promise<AuthResponse> => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  getMe: async (): Promise<User> => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

// Users API
export const usersAPI = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get('/users');
    return response.data;
  },
  create: async (userData: Partial<User> & { password: string }): Promise<User> => {
    const response = await api.post('/users', userData);
    return response.data;
  },
  getById: async (id: string): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },
  update: async (id: string, userData: Partial<User>): Promise<User> => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
  resetPassword: async (id: string, newPassword: string): Promise<void> => {
    await api.put(`/users/${id}/reset-password`, { newPassword });
  },
  updateAvatar: async (avatarBase64: string): Promise<{ avatarUrl: string }> => {
    const response = await api.put('/users/me/avatar', { avatar: avatarBase64 });
    return response.data;
  }
};

// Materials API
export const materialsAPI = {
  getAll: async (): Promise<Material[]> => {
    const response = await api.get('/materials');
    return response.data;
  },
  getById: async (id: string): Promise<Material> => {
    const response = await api.get(`/materials/${id}`);
    return response.data;
  },
  create: async (materialData: Partial<Material>): Promise<Material> => {
    const response = await api.post('/materials', materialData);
    return response.data;
  },
  update: async (id: string, materialData: Partial<Material>): Promise<Material> => {
    const response = await api.put(`/materials/${id}`, materialData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/materials/${id}`);
  }
};

// Resins API (Sarze)
export const resinsAPI = {
  getAll: async (): Promise<Resin[]> => {
    const response = await api.get('/resins');
    return response.data;
  },
  getById: async (id: string): Promise<Resin> => {
    const response = await api.get(`/resins/${id}`);
    return response.data;
  },
  create: async (resinData: Partial<Resin>): Promise<Resin> => {
    const response = await api.post('/resins', resinData);
    return response.data;
  },
  update: async (id: string, resinData: Partial<Resin>): Promise<Resin> => {
    const response = await api.put(`/resins/${id}`, resinData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/resins/${id}`);
  }
};

// Consumption API
export const consumptionAPI = {
  getAll: async (date?: string): Promise<Consumption[]> => {
    const params = date ? { date } : {};
    const response = await api.get('/consumption', { params });
    return response.data;
  },
  getArchived: async (): Promise<Consumption[]> => {
    const response = await api.get('/consumption/archived');
    return response.data;
  },
  getById: async (id: string): Promise<Consumption> => {
    const response = await api.get(`/consumption/${id}`);
    return response.data;
  },
  create: async (consumptionData: Partial<Consumption>): Promise<Consumption> => {
    const response = await api.post('/consumption', consumptionData);
    return response.data;
  },
  update: async (id: string, consumptionData: Partial<Consumption>): Promise<Consumption> => {
    const response = await api.put(`/consumption/${id}`, consumptionData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/consumption/${id}`);
  },
  archiveAll: async (): Promise<{ message: string; archivedCount: number }> => {
    const response = await api.post('/consumption/archive-all');
    return response.data;
  }
};

// Logistics bulk operations (admin / logistika)
export const logisticsBulkAPI = {
  clearAll: async (): Promise<{
    message: string;
    deleted: { consumption: number; receipts: number; disposals: number };
  }> => {
    const response = await api.delete('/logistics-bulk/clear-all');
    return response.data;
  }
};

// Inventory API
export const inventoryAPI = {
  getAll: async (): Promise<Inventory[]> => {
    const response = await api.get('/inventory');
    return response.data;
  },
  getByMaterialId: async (materialId: string): Promise<Inventory> => {
    const response = await api.get(`/inventory/material/${materialId}`);
    return response.data;
  },
  updateTotalWeight: async (materialId: string, totalWeight: number): Promise<Inventory> => {
    const response = await api.put(`/inventory/material/${materialId}`, { totalWeight });
    return response.data;
  },
  createForMaterial: async (materialId: string, totalWeight: number): Promise<Inventory> => {
    const response = await api.post(`/inventory/material/${materialId}`, { totalWeight });
    return response.data;
  }
};

// Receipts API
export const receiptsAPI = {
  getAll: async (): Promise<Receipt[]> => {
    const response = await api.get('/receipts');
    return response.data;
  },
  getById: async (id: string): Promise<Receipt> => {
    const response = await api.get(`/receipts/${id}`);
    return response.data;
  },
  getByMaterialId: async (materialId: string): Promise<Receipt[]> => {
    const response = await api.get(`/receipts/material/${materialId}`);
    return response.data;
  },
  create: async (receiptData: Partial<Receipt>): Promise<Receipt> => {
    const response = await api.post('/receipts', receiptData);
    return response.data;
  },
  update: async (id: string, receiptData: Partial<Receipt>): Promise<Receipt> => {
    const response = await api.put(`/receipts/${id}`, receiptData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/receipts/${id}`);
  }
};

// Disposals API
export const disposalsAPI = {
  getAll: async (): Promise<Disposal[]> => {
    const response = await api.get('/disposals');
    return response.data;
  },
  getById: async (id: string): Promise<Disposal> => {
    const response = await api.get(`/disposals/${id}`);
    return response.data;
  },
  getByMaterialId: async (materialId: string): Promise<Disposal[]> => {
    const response = await api.get(`/disposals/material/${materialId}`);
    return response.data;
  },
  getByReason: async (reason: string): Promise<Disposal[]> => {
    const response = await api.get(`/disposals/reason/${reason}`);
    return response.data;
  },
  create: async (disposalData: Partial<Disposal>): Promise<Disposal> => {
    const response = await api.post('/disposals', disposalData);
    return response.data;
  },
  update: async (id: string, disposalData: Partial<Disposal>): Promise<Disposal> => {
    const response = await api.put(`/disposals/${id}`, disposalData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/disposals/${id}`);
  }
};

// Procurement (Nabavka i ponude) API
export const procurementAPI = {
  getAll: async (): Promise<ProcurementRequest[]> => {
    const response = await api.get('/procurement');
    return response.data;
  },
  getById: async (id: string): Promise<ProcurementRequest> => {
    const response = await api.get(`/procurement/${id}`);
    return response.data;
  },
  create: async (data: { items: ProcurementItem[]; notes?: string }): Promise<ProcurementRequest> => {
    const response = await api.post('/procurement', data);
    return response.data;
  },
  updateStatus: async (id: string, data: { status: ProcurementStatus | 'delivered'; rejectionReason?: string }): Promise<{
    message: string;
    request: ProcurementRequest;
  }> => {
    const response = await api.put(`/procurement/${id}`, data);
    return response.data;
  },
  confirmDelivery: async (id: string, data: { decision: 'confirm' | 'reject'; note?: string }): Promise<{
    message: string;
    request: ProcurementRequest;
  }> => {
    const response = await api.post(`/procurement/${id}/confirm-delivery`, data);
    return response.data;
  },
  updateAndResubmit: async (id: string, data: { items: ProcurementItem[]; notes?: string }): Promise<{
    message: string;
    request: ProcurementRequest;
  }> => {
    const response = await api.put(`/procurement/${id}/update`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/procurement/${id}`);
    return response.data;
  },
  getPendingCount: async (): Promise<{ pendingCount: number }> => {
    const response = await api.get('/procurement/notifications/pending-count');
    return response.data;
  },
  downloadRequestPdf: async (id: string): Promise<Blob> => {
    const response = await api.get(`/procurement/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },
  downloadDeliveryPdf: async (id: string): Promise<Blob> => {
    const response = await api.get(`/procurement/${id}/delivery-pdf`, { responseType: 'blob' });
    return response.data;
  }
};

// Logistics catalog API (categories + goods)
export const logisticsCategoriesAPI = {
  getAll: async (): Promise<LogisticsCategory[]> => {
    const response = await api.get('/logistics/categories');
    return response.data;
  },
  create: async (data: { name: string; description?: string; parentCategory?: string | null }): Promise<LogisticsCategory> => {
    const response = await api.post('/logistics/categories', data);
    return response.data;
  },
  update: async (id: string, data: Partial<{ name: string; description: string; parentCategory: string | null }>): Promise<LogisticsCategory> => {
    const response = await api.put(`/logistics/categories/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/logistics/categories/${id}`);
    return response.data;
  }
};

// =========================
// IT Equipment API
// =========================

export const equipmentAPI = {
  getAll: async (): Promise<Equipment[]> => {
    const response = await api.get('/equipment');
    return response.data;
  },
  getById: async (id: string): Promise<Equipment> => {
    const response = await api.get(`/equipment/${id}`);
    return response.data;
  },
  create: async (data: {
    name: string;
    type: string;
    brand: string;
    deviceModel: string;
    serialNumber: string;
    location: string;
    status?: string;
    quantity?: number;
    ipAddress?: string;
    macAddress?: string;
    description?: string;
    category?: string;
  }): Promise<Equipment> => {
    const response = await api.post('/equipment', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{
      name: string;
      type: string;
      brand: string;
      deviceModel: string;
      serialNumber: string;
      location: string;
      status: string;
      quantity: number;
      ipAddress: string;
      macAddress: string;
      description: string;
      category: string;
      ports: Equipment['ports'];
    }>
  ): Promise<Equipment> => {
    const response = await api.put(`/equipment/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/equipment/${id}`);
    return response.data;
  },
  assign: async (
    id: string,
    data: { userId?: string; expectedReturnDate?: string; quantity?: number }
  ): Promise<Equipment> => {
    const response = await api.post(`/equipment/${id}/assign`, data);
    return response.data;
  },
  return: async (
    id: string,
    data: { userId?: string; quantity?: number }
  ): Promise<Equipment> => {
    const response = await api.post(`/equipment/${id}/return`, data);
    return response.data;
  }
};

export const equipmentCategoriesAPI = {
  getAll: async (): Promise<EquipmentCategory[]> => {
    const response = await api.get('/equipment-categories');
    return response.data;
  },
  getById: async (id: string): Promise<EquipmentCategory> => {
    const response = await api.get(`/equipment-categories/${id}`);
    return response.data;
  },
  create: async (data: {
    name: string;
    description?: string;
    icon?: string;
    color?: string;
    isActive?: boolean;
  }): Promise<EquipmentCategory> => {
    const response = await api.post('/equipment-categories', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{ name: string; description: string; icon: string; color: string; isActive: boolean }>
  ): Promise<EquipmentCategory> => {
    const response = await api.put(`/equipment-categories/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/equipment-categories/${id}`);
    return response.data;
  }
};

// =========================
// IT Servers API
// =========================

export const serversAPI = {
  getAll: async (): Promise<Server[]> => {
    const response = await api.get('/servers');
    return response.data;
  },
  getById: async (id: string): Promise<Server> => {
    const response = await api.get(`/servers/${id}`);
    return response.data;
  },
  create: async (data: {
    name: string;
    hostname: string;
    ipAddress: string;
    macAddress?: string;
    operatingSystem: string;
    cpu: string;
    memory: string;
    storage: string;
    status?: string;
    location: string;
    purpose: string;
    description?: string;
  }): Promise<Server> => {
    const response = await api.post('/servers', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{
      name: string;
      hostname: string;
      ipAddress: string;
      macAddress: string;
      operatingSystem: string;
      cpu: string;
      memory: string;
      storage: string;
      status: string;
      location: string;
      purpose: string;
      description: string;
    }>
  ): Promise<Server> => {
    const response = await api.put(`/servers/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/servers/${id}`);
    return response.data;
  }
};

// =========================
// IT Computers API
// =========================

export const computersAPI = {
  getAll: async (): Promise<Computer[]> => {
    const response = await api.get('/computers');
    return response.data;
  },
  getById: async (id: string): Promise<Computer> => {
    const response = await api.get(`/computers/${id}`);
    return response.data;
  },
  create: async (data: {
    name: string;
    ipAddress?: string;
    macAddress?: string;
    location: string;
    department: string;
    user?: string;
    status?: ComputerStatus;
    equipmentId?: string;
    subnetId?: string;
  }): Promise<Computer> => {
    const response = await api.post('/computers', data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/computers/${id}`);
    return response.data;
  },
  createFromEquipment: async (data: { equipmentId: string; quantity: number }): Promise<{
    message: string;
    computers: Computer[];
  }> => {
    const response = await api.post('/computers/from-equipment', data);
    return response.data;
  }
};

// =========================
// Subnet Calculator API
// =========================

export interface SubnetCalculationResult {
  cidr: string;
  networkAddress: string;
  broadcastAddress: string;
  subnetMask: string;
  totalIPs: number;
  usableIPs: number;
  firstUsableIP: string;
  lastUsableIP: string;
  prefixLength: number;
}

export const subnetCalculatorAPI = {
  calculate: async (cidr: string): Promise<SubnetCalculationResult> => {
    const response = await api.post('/subnet-calculator/calculate', { cidr });
    return response.data;
  }
};

// =========================
// Subnets & IP Assignment API
// =========================

export interface Subnet {
  _id: string;
  name: string;
  cidr: string;
  gateway: string;
  dnsServers?: string[];
  description?: string;
  totalIPs: number;
  usedIPs: number;
  freeIPs: number;
}

export const subnetsAPI = {
  getAll: async (): Promise<Subnet[]> => {
    const response = await api.get('/subnets');
    return response.data;
  },
  getById: async (id: string): Promise<Subnet> => {
    const response = await api.get(`/subnets/${id}`);
    return response.data;
  }
};

export const ipAssignmentAPI = {
  getAvailableIPs: async (subnetId: string, limit = 50): Promise<{ availableIPs: string[] }> => {
    const response = await api.get(`/ip-assignment/available-ips/${subnetId}`, {
      params: { limit }
    });
    return response.data;
  }
};

// =========================
// IT Network Devices API
// =========================

export const networkDevicesAPI = {
  getAll: async (): Promise<NetworkDevice[]> => {
    const response = await api.get('/network-devices');
    return response.data;
  },
  getById: async (id: string): Promise<NetworkDevice> => {
    const response = await api.get(`/network-devices/${id}`);
    return response.data;
  },
  create: async (data: {
    name: string;
    type: string;
    deviceModel: string;
    serialNumber: string;
    location: string;
    status?: string;
    ipAddress?: string;
    macAddress?: string;
    description?: string;
  }): Promise<NetworkDevice> => {
    const response = await api.post('/network-devices', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{
      name: string;
      type: string;
      deviceModel: string;
      serialNumber: string;
      location: string;
      status: string;
      ipAddress: string;
      macAddress: string;
      description: string;
      ports: NetworkDevice['ports'];
    }>
  ): Promise<NetworkDevice> => {
    const response = await api.put(`/network-devices/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/network-devices/${id}`);
    return response.data;
  }
};

// =========================
// VLAN API
// =========================

export const vlansAPI = {
  getAll: async (): Promise<Vlan[]> => {
    const response = await api.get('/vlans');
    return response.data;
  },
  getById: async (id: string): Promise<Vlan> => {
    const response = await api.get(`/vlans/${id}`);
    return response.data;
  },
  create: async (data: {
    name: string;
    vlanId: number;
    description?: string;
    subnetId?: string;
    status?: string;
  }): Promise<Vlan> => {
    const response = await api.post('/vlans', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{
      name: string;
      vlanId: number;
      description: string;
      subnetId: string;
      status: string;
    }>
  ): Promise<Vlan> => {
    const response = await api.put(`/vlans/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/vlans/${id}`);
    return response.data;
  }
};

// =========================
// IT Incidents API
// =========================

export interface ITIncidentSummaryStats {
  total: number;
  byStatus: {
    open: number;
    in_progress: number;
    resolved: number;
    closed: number;
  };
  byPriority: {
    critical: number;
    high: number;
  };
  byCategory: Array<{ _id: ITIncidentCategory; count: number }>;
}

export const itIncidentsAPI = {
  getAll: async (params?: {
    status?: ITIncidentStatus;
    priority?: ITIncidentPriority;
    category?: ITIncidentCategory;
    assignedTo?: string;
  }): Promise<ITIncident[]> => {
    const response = await api.get('/it-incidents', { params });
    return response.data;
  },
  getById: async (id: string): Promise<ITIncident> => {
    const response = await api.get(`/it-incidents/${id}`);
    return response.data;
  },
  create: async (data: {
    title: string;
    description: string;
    category: ITIncidentCategory;
    priority: ITIncidentPriority;
    affectedUser?: string;
    affectedSystem?: string;
    location?: string;
    assignedTo?: string;
  }): Promise<ITIncident> => {
    const response = await api.post('/it-incidents', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{
      title: string;
      description: string;
      category: ITIncidentCategory;
      priority: ITIncidentPriority;
      status: ITIncidentStatus;
      affectedUser: string;
      affectedSystem: string;
      location: string;
      assignedTo: string | null;
      resolution: string;
    }>
  ): Promise<ITIncident> => {
    const response = await api.put(`/it-incidents/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/it-incidents/${id}`);
    return response.data;
  },
  getSummary: async (): Promise<ITIncidentSummaryStats> => {
    const response = await api.get('/it-incidents/stats/summary');
    return response.data;
  }
};

// =========================
// Hilti API
// =========================

export const hiltiWorkersAPI = {
  getAll: async (params?: { showInactive?: boolean }): Promise<HiltiWorker[]> => {
    const response = await api.get('/hilti/workers', { params });
    return response.data;
  },
  create: async (data: { firstName: string; lastName: string }): Promise<HiltiWorker> => {
    const response = await api.post('/hilti/workers', data);
    return response.data;
  },
  update: async (id: string, data: Partial<{ firstName: string; lastName: string }>): Promise<HiltiWorker> => {
    const response = await api.put(`/hilti/workers/${id}`, data);
    return response.data;
  },
  deactivate: async (id: string): Promise<HiltiWorker> => {
    const response = await api.post(`/hilti/workers/${id}/deactivate`);
    return response.data;
  },
  reactivate: async (id: string): Promise<HiltiWorker> => {
    const response = await api.post(`/hilti/workers/${id}/reactivate`);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/hilti/workers/${id}`);
    return response.data;
  }
};

export const hiltiProductsAPI = {
  getAll: async (): Promise<HiltiProduct[]> => {
    const response = await api.get('/hilti/products');
    return response.data;
  },
  create: async (data: { name: string; materials?: Array<{ materialId: string; quantityPerUnit: number }> }): Promise<HiltiProduct> => {
    const response = await api.post('/hilti/products', data);
    return response.data;
  },
  update: async (
    id: string,
    data: { name?: string; materials?: Array<{ materialId: string; quantityPerUnit: number }> }
  ): Promise<HiltiProduct> => {
    const response = await api.put(`/hilti/products/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/hilti/products/${id}`);
    return response.data;
  }
};

export const hiltiMaterialsAPI = {
  getAllForProduct: async (
    productOrParams: string | { productId: string; startDate?: string; endDate?: string }
  ): Promise<HiltiMaterial[]> => {
    const params =
      typeof productOrParams === 'string'
        ? { productId: productOrParams }
        : productOrParams;
    const response = await api.get('/hilti/materials', { params });
    return response.data;
  },
  create: async (data: {
    productId: string;
    name: string;
    unit?: string;
    initialStock?: number;
    minStock?: number;
    description?: string;
  }): Promise<HiltiMaterial> => {
    const response = await api.post('/hilti/materials', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{
      name: string;
      unit: string;
      minStock: number;
      description: string;
      currentStock: number;
    }>
  ): Promise<HiltiMaterial> => {
    const response = await api.put(`/hilti/materials/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/hilti/materials/${id}`);
    return response.data;
  }
};

export const hiltiMaterialReceiptsAPI = {
  getAll: async (params: { productId: string; materialId?: string }): Promise<HiltiMaterialReceipt[]> => {
    const response = await api.get('/hilti/materials/receipts', { params });
    return response.data;
  },
  create: async (data: {
    productId: string;
    materialId: string;
    receiptDate: string;
    receiptTime: string;
    quantity: number;
    unit?: string;
    supplier?: string;
    transporter?: string;
    reference?: string;
    notes?: string;
  }): Promise<HiltiMaterialReceipt> => {
    const response = await api.post('/hilti/materials/receipts', data);
    return response.data;
  },
  update: async (
    id: string,
    data: {
      productId: string;
      materialId: string;
      receiptDate: string;
      receiptTime: string;
      quantity: number;
      unit?: string;
      supplier?: string;
      transporter?: string;
      reference?: string;
      notes?: string;
    }
  ): Promise<HiltiMaterialReceipt> => {
    const response = await api.put(`/hilti/materials/receipts/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/hilti/materials/receipts/${id}`);
    return response.data;
  }
};

export const hiltiMaterialDisposalsAPI = {
  getAll: async (params: { productId: string; materialId?: string }): Promise<HiltiMaterialDisposal[]> => {
    const response = await api.get('/hilti/materials/disposals', { params });
    return response.data;
  },
  create: async (data: {
    productId: string;
    materialId: string;
    disposalDate: string;
    disposalTime: string;
    quantity: number;
    unit?: string;
    reason: string;
    notes?: string;
  }): Promise<HiltiMaterialDisposal> => {
    const response = await api.post('/hilti/materials/disposals', data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/hilti/materials/disposals/${id}`);
    return response.data;
  }
};

export const hiltiProductionAPI = {
  getAll: async (params?: { showAll?: boolean; startDate?: string; endDate?: string }): Promise<HiltiProductionRecord[]> => {
    const response = await api.get('/hilti/production', { params });
    return response.data;
  },
  create: async (data: {
    productId: string;
    quantity: number;
    unit: 'kom' | 'paleta';
    producedDate?: string;
    shiftSupervisor: string;
    shift: 'I' | 'II' | 'III';
    notes?: string;
  }): Promise<HiltiProductionRecord> => {
    const response = await api.post('/hilti/production', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{
      productId: string;
      quantity: number;
      unit: 'kom' | 'paleta';
      producedDate: string;
      shiftSupervisor: string;
      shift: 'I' | 'II' | 'III';
      notes: string;
    }>
  ): Promise<HiltiProductionRecord> => {
    const response = await api.put(`/hilti/production/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/hilti/production/${id}`);
    return response.data;
  }
};

export const hiltiDailyWorkAPI = {
  getAll: async (params?: { date?: string; startDate?: string; endDate?: string }): Promise<HiltiDailyWorkRecord[]> => {
    const response = await api.get('/hilti/daily-work', { params });
    return response.data;
  },
  create: async (data: {
    workerId: string;
    date: string; // YYYY-MM-DD
    products: Array<{ productId: string; quantity: number }>;
    notes?: string;
  }): Promise<HiltiDailyWorkRecord> => {
    const response = await api.post('/hilti/daily-work', data);
    return response.data;
  },
  update: async (
    id: string,
    data: {
      workerId?: string;
      date?: string;
      products?: Array<{ productId: string; quantity: number }>;
      notes?: string;
    }
  ): Promise<HiltiDailyWorkRecord> => {
    const response = await api.put(`/hilti/daily-work/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/hilti/daily-work/${id}`);
    return response.data;
  }
};

export const logisticsGoodsAPI = {
  getAll: async (params?: { categoryId?: string; search?: string }): Promise<LogisticsGood[]> => {
    const response = await api.get('/logistics/goods', { params });
    return response.data;
  },
  getById: async (id: string): Promise<LogisticsGood> => {
    const response = await api.get(`/logistics/goods/${id}`);
    return response.data;
  },
  create: async (data: {
    name: string;
    code?: string;
    description?: string;
    unit?: string;
    categoryId: string;
    suppliers?: any[];
  }): Promise<LogisticsGood> => {
    const response = await api.post('/logistics/goods', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{
      name: string;
      code: string;
      description: string;
      unit: string;
      categoryId: string;
      suppliers: any[];
    }>
  ): Promise<LogisticsGood> => {
    const response = await api.put(`/logistics/goods/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/logistics/goods/${id}`);
    return response.data;
  }
};

// =========================
// Magacin (Warehouse) API
// =========================
export const magacinProductsAPI = {
  getAll: async (): Promise<MagacinProduct[]> => {
    const response = await api.get('/magacin/products');
    return response.data;
  },
  getById: async (id: string): Promise<MagacinProduct> => {
    const response = await api.get(`/magacin/products/${id}`);
    return response.data;
  },
  create: async (data: Partial<MagacinProduct>): Promise<MagacinProduct> => {
    const response = await api.post('/magacin/products', data);
    return response.data;
  },
  update: async (id: string, data: Partial<MagacinProduct>): Promise<MagacinProduct> => {
    const response = await api.put(`/magacin/products/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/magacin/products/${id}`);
    return response.data;
  }
};

export const magacinProductTypesAPI = {
  getAll: async (): Promise<MagacinProductType[]> => {
    const response = await api.get('/magacin/product-types');
    return response.data;
  },
  getById: async (id: string): Promise<MagacinProductType> => {
    const response = await api.get(`/magacin/product-types/${id}`);
    return response.data;
  },
  create: async (data: Partial<MagacinProductType>): Promise<MagacinProductType> => {
    const response = await api.post('/magacin/product-types', data);
    return response.data;
  },
  update: async (id: string, data: Partial<MagacinProductType>): Promise<MagacinProductType> => {
    const response = await api.put(`/magacin/product-types/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/magacin/product-types/${id}`);
    return response.data;
  }
};

export const magacinArrivalsAPI = {
  getAll: async (): Promise<MagacinArrival[]> => {
    const response = await api.get('/magacin/arrivals');
    return response.data;
  },
  getByProductId: async (productId: string): Promise<MagacinArrival[]> => {
    const response = await api.get(`/magacin/arrivals/product/${productId}`);
    return response.data;
  },
  create: async (data: Partial<MagacinArrival> & { productId: string; quantity: number }): Promise<MagacinArrival> => {
    const response = await api.post('/magacin/arrivals', data);
    return response.data;
  },
  update: async (id: string, data: Partial<MagacinArrival>): Promise<MagacinArrival> => {
    const response = await api.put(`/magacin/arrivals/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/magacin/arrivals/${id}`);
    return response.data;
  }
};

export const magacinRemovalsAPI = {
  getAll: async (): Promise<MagacinRemoval[]> => {
    const response = await api.get('/magacin/removals');
    return response.data;
  },
  getById: async (id: string): Promise<MagacinRemoval> => {
    const response = await api.get(`/magacin/removals/${id}`);
    return response.data;
  },
  create: async (data: Partial<MagacinRemoval> & { quantity: number }): Promise<MagacinRemoval> => {
    const response = await api.post('/magacin/removals', data);
    return response.data;
  },
  update: async (id: string, data: Partial<MagacinRemoval>): Promise<MagacinRemoval> => {
    const response = await api.put(`/magacin/removals/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/magacin/removals/${id}`);
    return response.data;
  }
};

export const magacinLoansAPI = {
  getAll: async (): Promise<MagacinLoan[]> => {
    const response = await api.get('/magacin/loans');
    return response.data;
  },
  getById: async (id: string): Promise<MagacinLoan> => {
    const response = await api.get(`/magacin/loans/${id}`);
    return response.data;
  },
  create: async (data: Partial<MagacinLoan> & { productId: string; quantity: number }): Promise<MagacinLoan> => {
    const response = await api.post('/magacin/loans', data);
    return response.data;
  },
  update: async (id: string, data: Partial<MagacinLoan>): Promise<MagacinLoan> => {
    const response = await api.put(`/magacin/loans/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/magacin/loans/${id}`);
    return response.data;
  }
};

// =========================
// Magacin – Ulaz (Receivings) API
// =========================
export const magacinReceivingsAPI = {
  getAll: async (params?: { status?: MagacinReceivingStatus | MagacinReceivingStatus[]; mine?: boolean }): Promise<MagacinReceiving[]> => {
    const query: Record<string, string> = {};
    if (params?.status) {
      query.status = Array.isArray(params.status) ? params.status.join(',') : params.status;
    }
    if (params?.mine) {
      query.mine = 'true';
    }
    const response = await api.get('/magacin/receivings', { params: query });
    return response.data;
  },
  getById: async (id: string): Promise<MagacinReceiving> => {
    const response = await api.get(`/magacin/receivings/${id}`);
    return response.data;
  },
  create: async (data: {
    deliveryNoteName: string;
    items: Array<{
      productTypeId: string;
      productName: string;
      quantity: number;
      unit: string;
      arrivalDate: string | Date;
      productClass?: 'A' | 'B' | 'C';
      supplier?: string;
      batchNumber?: string;
      notes?: string;
    }>;
    issues?: Array<{
      type: MagacinReceivingIssue['type'];
      productName?: string;
      quantity?: number;
      unit?: string;
      description: string;
    }>;
    notes?: string;
  }): Promise<MagacinReceiving> => {
    const response = await api.post('/magacin/receivings', data);
    return response.data;
  },
  update: async (id: string, data: Partial<{
    deliveryNoteName: string;
    items: Array<{
      productTypeId: string;
      productName: string;
      quantity: number;
      unit: string;
      arrivalDate: string | Date;
      productClass?: 'A' | 'B' | 'C';
      supplier?: string;
      batchNumber?: string;
      notes?: string;
    }>;
    notes: string;
  }>): Promise<MagacinReceiving> => {
    const response = await api.put(`/magacin/receivings/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/magacin/receivings/${id}`);
    return response.data;
  },
  addIssue: async (
    id: string,
    issue: { type: MagacinReceivingIssue['type']; productName?: string; quantity?: number; unit?: string; description: string }
  ): Promise<MagacinReceiving> => {
    const response = await api.post(`/magacin/receivings/${id}/issues`, issue);
    return response.data;
  },
  removeIssue: async (id: string, issueId: string): Promise<MagacinReceiving> => {
    const response = await api.delete(`/magacin/receivings/${id}/issues/${issueId}`);
    return response.data;
  },
  approve: async (id: string, approvalNotes?: string): Promise<MagacinReceiving> => {
    const response = await api.post(`/magacin/receivings/${id}/approve`, { approvalNotes: approvalNotes || '' });
    return response.data;
  },
  reject: async (id: string, rejectionReason: string): Promise<MagacinReceiving> => {
    const response = await api.post(`/magacin/receivings/${id}/reject`, { rejectionReason });
    return response.data;
  }
};

// =========================
// Lager gotovih proizvoda API
// =========================
export const lagerCompaniesAPI = {
  getAll: async (): Promise<LagerCompany[]> => {
    const response = await api.get('/lager/companies');
    return response.data;
  },
  getById: async (id: string): Promise<LagerCompany> => {
    const response = await api.get(`/lager/companies/${id}`);
    return response.data;
  },
  create: async (data: Partial<LagerCompany>): Promise<LagerCompany> => {
    const response = await api.post('/lager/companies', data);
    return response.data;
  },
  update: async (id: string, data: Partial<LagerCompany>): Promise<LagerCompany> => {
    const response = await api.put(`/lager/companies/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/lager/companies/${id}`);
    return response.data;
  }
};

export const lagerFinishedProductsAPI = {
  getAll: async (): Promise<LagerFinishedProduct[]> => {
    const response = await api.get('/lager/finished-products');
    return response.data;
  },
  getById: async (id: string): Promise<LagerFinishedProduct> => {
    const response = await api.get(`/lager/finished-products/${id}`);
    return response.data;
  },
  create: async (data: Partial<LagerFinishedProduct>): Promise<LagerFinishedProduct> => {
    const response = await api.post('/lager/finished-products', data);
    return response.data;
  },
  update: async (id: string, data: Partial<LagerFinishedProduct>): Promise<LagerFinishedProduct> => {
    const response = await api.put(`/lager/finished-products/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/lager/finished-products/${id}`);
    return response.data;
  }
};

export const lagerTransportAPI = {
  getAll: async (): Promise<LagerTransport[]> => {
    const response = await api.get('/lager/transport');
    return response.data;
  },
  getById: async (id: string): Promise<LagerTransport> => {
    const response = await api.get(`/lager/transport/${id}`);
    return response.data;
  },
  create: async (
    data: Partial<LagerTransport> & { productId: string; companyId: string; quantity: number; unit: string; shippedDate: string }
  ): Promise<LagerTransport> => {
    const response = await api.post('/lager/transport', data);
    return response.data;
  },
  update: async (id: string, data: Partial<LagerTransport>): Promise<LagerTransport> => {
    const response = await api.put(`/lager/transport/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/lager/transport/${id}`);
    return response.data;
  }
};

export const lagerProductionAPI = {
  getAll: async (params?: { date?: string; showAll?: boolean; lastDays?: number }): Promise<LagerProductionRecord[]> => {
    const response = await api.get('/lager/daily-production', { params });
    return response.data;
  },
  create: async (
    data: Partial<LagerProductionRecord> & { productId: string; quantity: number; unit: string; shiftSupervisor: string; shift: string }
  ): Promise<LagerProductionRecord> => {
    const response = await api.post('/lager/daily-production', data);
    return response.data;
  },
  update: async (id: string, data: Partial<LagerProductionRecord>): Promise<LagerProductionRecord> => {
    const response = await api.put(`/lager/daily-production/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/lager/daily-production/${id}`);
    return response.data;
  }
};

// Project Folders API
export const projectFoldersAPI = {
  getAll: async (): Promise<ProjectFolder[]> => {
    const response = await api.get('/project-folders');
    return response.data;
  },
  getById: async (id: string): Promise<ProjectFolder> => {
    const response = await api.get(`/project-folders/${id}`);
    return response.data;
  },
  getBySlug: async (slug: string): Promise<ProjectFolder> => {
    const response = await api.get(`/project-folders/slug/${slug}`);
    return response.data;
  },
  create: async (folderData: Partial<ProjectFolder>): Promise<ProjectFolder> => {
    const response = await api.post('/project-folders', folderData);
    return response.data;
  },
  update: async (id: string, folderData: Partial<ProjectFolder>): Promise<ProjectFolder> => {
    const response = await api.put(`/project-folders/${id}`, folderData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/project-folders/${id}`);
  }
};

// Projects API
export const projectsAPI = {
  getAll: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    urgent?: 'urgent' | 'not-urgent' | boolean;
    folderId?: string;
    status?: string | string[];
    excludeStatus?: string | string[];
  }): Promise<Project[] | { items: Project[]; pagination: { page: number; limit: number; total: number; pages: number } }> => {
    const response = await api.get('/projects', { params });
    return response.data;
  },
  getById: async (id: string): Promise<Project> => {
    const response = await api.get(`/projects/${id}`);
    return response.data;
  },
  create: async (projectData: Omit<Partial<Project>, 'team'> & { folder?: string | null; team?: string[] }): Promise<Project> => {
    const response = await api.post('/projects', projectData);
    return response.data;
  },
  update: async (id: string, projectData: Omit<Partial<Project>, 'team'> & { folder?: string | null; team?: string[] }): Promise<Project> => {
    const response = await api.put(`/projects/${id}`, projectData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/projects/${id}`);
  },
  updateStatus: async (id: string, status: string): Promise<Project> => {
    const response = await api.put(`/projects/${id}/status`, { status });
    return response.data;
  }
};

// Tasks API
export const tasksAPI = {
  getAll: async (params?: {
    projectId?: string;
    status?: 'todo' | 'in_progress' | 'done';
    priority?: 'low' | 'medium' | 'high';
    assignees?: string[];
    page?: number;
    limit?: number;
  }): Promise<Task[] | { items: Task[]; pagination: { page: number; limit: number; total: number; pages: number } }> => {
    const response = await api.get('/tasks', { params });
    return response.data;
  },
  getById: async (id: string): Promise<Task> => {
    const response = await api.get(`/tasks/${id}`);
    return response.data;
  },
  create: async (taskData: Omit<Partial<Task>, 'assignees'> & { projectId: string; assignees: string[] }): Promise<Task> => {
    const response = await api.post('/tasks', taskData);
    return response.data;
  },
  update: async (id: string, taskData: Omit<Partial<Task>, 'assignees'> & { assignees?: string[] }): Promise<Task> => {
    const response = await api.put(`/tasks/${id}`, taskData);
    return response.data;
  },
  updateStatus: async (id: string, status: 'todo' | 'in_progress' | 'done'): Promise<Task> => {
    const response = await api.put(`/tasks/${id}/status`, { status });
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/tasks/${id}`);
  }
};

// Comments API
export const commentsAPI = {
  getByTask: async (taskId: string): Promise<Comment[]> => {
    const response = await api.get(`/comments/task/${taskId}`);
    return response.data;
  },
  create: async (commentData: { text: string; taskId: string; mentions?: string[] }): Promise<Comment> => {
    const response = await api.post('/comments', commentData);
    return response.data;
  },
  update: async (id: string, commentData: { text: string; mentions?: string[] }): Promise<Comment> => {
    const response = await api.put(`/comments/${id}`, commentData);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/comments/${id}`);
  }
};

// IT Tickets API
export const itTicketsAPI = {
  getAll: async (status?: ITTicketStatus): Promise<ITTicket[]> => {
    const params = status ? { status } : {};
    const response = await api.get('/it-tickets', { params });
    return response.data;
  },
  getById: async (id: string): Promise<ITTicket> => {
    const response = await api.get(`/it-tickets/${id}`);
    return response.data;
  },
  create: async (ticketData: { problemType: ITTicketProblemType; description: string }): Promise<ITTicket> => {
    const response = await api.post('/it-tickets', ticketData);
    return response.data;
  },
  updateStatus: async (id: string, status: ITTicketStatus): Promise<ITTicket> => {
    const response = await api.put(`/it-tickets/${id}/status`, { status });
    return response.data;
  },
  addComment: async (id: string, text: string): Promise<ITTicket> => {
    const response = await api.post(`/it-tickets/${id}/comments`, { text });
    return response.data;
  },
  updateComment: async (ticketId: string, commentId: string, text: string): Promise<ITTicket> => {
    const response = await api.put(`/it-tickets/${ticketId}/comments/${commentId}`, { text });
    return response.data;
  },
  getStats: async (): Promise<{ count: number }> => {
    const response = await api.get('/it-tickets/stats/count');
    return response.data;
  }
};

// Announcements API
export const announcementsAPI = {
  create: async (announcementData: { title: string; message: string }): Promise<{ announcement: Announcement }> => {
    const response = await api.post('/announcements', announcementData);
    return response.data;
  },
  getActive: async (): Promise<{ announcement: Announcement | null }> => {
    const response = await api.get('/announcements/active');
    return response.data;
  },
  acknowledge: async (announcementId: string): Promise<void> => {
    await api.post(`/announcements/${announcementId}/acknowledge`);
  }
};

// =========================
// Attendance (Evidencija prisustva) API
// =========================
export const attendanceAPI = {
  // Workers
  getWorkers: async (params?: { isActive?: boolean | null }): Promise<Worker[]> => {
    const queryParams =
      params?.isActive === null || params?.isActive === undefined
        ? undefined
        : { isActive: params.isActive.toString() };
    const response = await api.get('/attendance/workers', { params: queryParams });
    return response.data;
  },
  getWorkerById: async (id: string): Promise<Worker> => {
    const response = await api.get(`/attendance/workers/${id}`);
    return response.data;
  },
  createWorker: async (data: {
    firstName: string;
    lastName: string;
    employeeId?: string;
    positionId: string;
    dateOfEmployment: string;
    annualLeaveDaysNew?: number;
    slavaName?: string;
    slavaMonth?: number;
    slavaDay?: number;
  }): Promise<Worker> => {
    const response = await api.post('/attendance/workers', data);
    return response.data;
  },
  updateWorker: async (
    id: string,
    data: Partial<{
      firstName: string;
      lastName: string;
      employeeId: string | null;
      positionId: string;
      dateOfEmployment: string;
      isActive: boolean;
      slavaName: string | null;
      slavaMonth: number | null;
      slavaDay: number | null;
    }>
  ): Promise<Worker> => {
    const response = await api.put(`/attendance/workers/${id}`, data);
    return response.data;
  },
  deactivateWorker: async (
    id: string,
    data?: { deactivationReason?: string; endDate?: string }
  ): Promise<{ message: string; worker: Worker }> => {
    const response = await api.delete(`/attendance/workers/${id}`, { data });
    return response.data;
  },
  reactivateWorker: async (
    id: string,
    data?: { reactivationDate?: string }
  ): Promise<{ message: string; worker: Worker }> => {
    const response = await api.patch(`/attendance/workers/${id}/reactivate`, data || {});
    return response.data;
  },
  deleteWorkerPermanent: async (
    id: string
  ): Promise<{ message: string; deletedWorker: { firstName: string; lastName: string; employeeId?: string } }> => {
    const response = await api.delete(`/attendance/workers/${id}/permanent`);
    return response.data;
  },

  // Positions
  getPositions: async (params?: { isActive?: boolean }): Promise<Position[]> => {
    const queryParams = params?.isActive !== undefined ? { isActive: params.isActive.toString() } : undefined;
    const response = await api.get('/attendance/positions', { params: queryParams });
    return response.data;
  },
  createPosition: async (data: { name: string; description?: string }): Promise<Position> => {
    const response = await api.post('/attendance/positions', data);
    return response.data;
  },
  updatePosition: async (
    id: string,
    data: Partial<{ name: string; description: string; isActive: boolean }>
  ): Promise<Position> => {
    const response = await api.put(`/attendance/positions/${id}`, data);
    return response.data;
  },
  deletePosition: async (id: string): Promise<{ message: string; position: Position }> => {
    const response = await api.delete(`/attendance/positions/${id}`);
    return response.data;
  },

  // Attendance Records
  getAttendanceRecordsForWorker: async (params: { workerId: string; startDate?: string; endDate?: string }): Promise<AttendanceRecord[]> => {
    const { workerId, ...rest } = params;
    const response = await api.get(`/attendance/records/worker/${workerId}`, { params: rest });
    return response.data;
  },
  // Reports
  getMonthlyReportAll: async (year: number, month: number): Promise<any> => {
    const response = await api.get(`/attendance/reports/monthly-all/${year}/${month}`);
    return response.data;
  },
  getMonthlyReportForWorker: async (workerId: string, year: number, month: number): Promise<any> => {
    const response = await api.get(`/attendance/reports/monthly/${workerId}/${year}/${month}`);
    return response.data;
  },
  getDailyRecords: async (dateIsoOrYmd: string): Promise<any[]> => {
    const response = await api.get(`/attendance/records/date/${dateIsoOrYmd}`);
    return response.data;
  },
  upsertAttendanceRecord: async (data: {
    workerId: string;
    date: string;
    hoursWorked: number;
    overtimeHours: number;
    isAnnualLeave: boolean;
    isSickLeave: boolean;
    isSlava: boolean;
    notes?: string;
  }): Promise<any> => {
    const response = await api.post('/attendance/records', data);
    return response.data;
  },
  getWorkerPeriodsForDate: async (dateIsoOrYmd: string): Promise<any[]> => {
    const response = await api.get(`/attendance/workers/periods/${dateIsoOrYmd}`);
    return response.data;
  },
  cleanupOrphanedRecords: async (): Promise<any> => {
    const response = await api.post('/attendance/cleanup-orphaned-records', {});
    return response.data;
  },
  // Individual absence types
  getAbsenceTypes: async (params?: { isActive?: boolean }): Promise<AbsenceType[]> => {
    const queryParams = params?.isActive !== undefined ? { isActive: params.isActive.toString() } : undefined;
    const response = await api.get('/attendance/absence-types', { params: queryParams });
    return response.data;
  },
  createAbsenceType: async (data: { name: string; type: 'paid' | 'unpaid'; defaultDays: number }): Promise<AbsenceType> => {
    const response = await api.post('/attendance/absence-types', data);
    return response.data;
  },
  updateAbsenceType: async (
    id: string,
    data: Partial<{ name: string; type: 'paid' | 'unpaid'; defaultDays: number; isActive: boolean }>
  ): Promise<AbsenceType> => {
    const response = await api.put(`/attendance/absence-types/${id}`, data);
    return response.data;
  },
  deleteAbsenceType: async (id: string): Promise<{ message: string; absenceType: AbsenceType }> => {
    const response = await api.delete(`/attendance/absence-types/${id}`);
    return response.data;
  },

  // Sick Leave
  startSickLeave: async (data: { workerId: string; startDate: string; diagnosis?: string; excludeWeekends?: boolean; notes?: string }): Promise<any> => {
    const response = await api.post('/attendance/sick-leave/start', data);
    return response.data;
  },
  endSickLeave: async (data: { workerId: string; endDate: string; notes?: string }): Promise<any> => {
    const response = await api.post('/attendance/sick-leave/end', data);
    return response.data;
  },
  getSickLeaveHistory: async (workerId: string): Promise<SickLeaveHistory[]> => {
    const response = await api.get(`/attendance/sick-leave/worker/${workerId}`);
    return response.data;
  },
  getSickLeaveHistoryYear: async (workerId: string, year: number): Promise<SickLeaveHistory[]> => {
    const response = await api.get(`/attendance/sick-leave/worker/${workerId}/year/${year}`);
    return response.data;
  },

  // Annual Leave
  createAnnualLeave: async (data: { workerId: string; startDate: string; numberOfDays: number; leaveType: 'old' | 'new'; notes?: string; excludeWeekends?: boolean }): Promise<AnnualLeaveHistory> => {
    const response = await api.post('/attendance/annual-leave', data);
    return response.data;
  },
  getAnnualLeaveHistory: async (workerId: string): Promise<AnnualLeaveHistory[]> => {
    const response = await api.get(`/attendance/annual-leave/worker/${workerId}`);
    return response.data;
  },
  getAnnualLeaveHistoryYear: async (workerId: string, year: number): Promise<AnnualLeaveHistory[]> => {
    const response = await api.get(`/attendance/annual-leave/worker/${workerId}/year/${year}`);
    return response.data;
  },

  // Paid/Unpaid Leave (per-worker)
  createPaidUnpaidLeave: async (data: { workerId: string; startDate: string; leaveType: 'paid' | 'unpaid'; numberOfDays: number; reason: string; excludeWeekends?: boolean; notes?: string }): Promise<PaidUnpaidLeaveHistory> => {
    const response = await api.post('/attendance/paid-unpaid-leave', data);
    return response.data;
  },
  getPaidUnpaidLeaveHistoryYear: async (workerId: string, year: number): Promise<PaidUnpaidLeaveHistory[]> => {
    const response = await api.get(`/attendance/paid-unpaid-leave/worker/${workerId}/year/${year}`);
    return response.data;
  }
};

// Absence (Individualna odsustva + praznici) API
export const absenceAPI = {
  getAll: async (params?: { isActive?: boolean; type?: 'paid' | 'unpaid' }): Promise<Absence[]> => {
    const queryParams: any = {};
    if (params?.isActive !== undefined) queryParams.isActive = params.isActive.toString();
    if (params?.type) queryParams.type = params.type;
    const response = await api.get('/absence', { params: Object.keys(queryParams).length ? queryParams : undefined });
    return response.data;
  },
  getWorkerAbsenceHistory: async (workerId: string): Promise<Absence[]> => {
    const response = await api.get(`/absence/worker/${workerId}/history`);
    return response.data;
  },
  create: async (data: {
    name: string;
    category: 'holiday' | 'absence';
    type: 'paid' | 'unpaid';
    startDate: string;
    endDate: string;
    appliesToAll: boolean;
    isRange?: boolean;
    description?: string;
    deductFromAnnualLeave?: boolean;
    excludeWeekends?: boolean;
    isRecurring?: boolean;
    recurringMonth?: number;
    recurringDay?: number;
    recurringEndMonth?: number;
    recurringEndDay?: number;
    workers?: string[];
    positions?: string[];
  }): Promise<Absence> => {
    const response = await api.post('/absence', data);
    return response.data;
  },
  update: async (
    id: string,
    data: Partial<{
      name: string;
      category: 'holiday' | 'absence';
      type: 'paid' | 'unpaid';
      startDate: string;
      endDate: string;
      isRange: boolean;
      description: string;
      appliesToAll: boolean;
      deductFromAnnualLeave: boolean;
      excludeWeekends: boolean;
      isRecurring: boolean;
      recurringMonth: number;
      recurringDay: number;
      recurringEndMonth: number;
      recurringEndDay: number;
      workers: string[];
      positions: string[];
    }>
  ): Promise<Absence> => {
    const response = await api.put(`/absence/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/absence/${id}`);
    return response.data;
  },
  toggle: async (id: string): Promise<{ message: string; absence: Absence }> => {
    const response = await api.patch(`/absence/${id}/toggle`, {});
    return response.data;
  }
};

// Finance (Delovodnik) - Companies API
export const financeCompaniesAPI = {
  getAll: async (params?: { isActive?: boolean }): Promise<FinanceCompany[]> => {
    const response = await api.get('/finance/companies', {
      params: params?.isActive !== undefined ? { isActive: params.isActive.toString() } : undefined
    });
    return response.data;
  },
  getById: async (id: string): Promise<FinanceCompany> => {
    const response = await api.get(`/finance/companies/${id}`);
    return response.data;
  },
  create: async (data: Pick<FinanceCompany, 'name' | 'country' | 'city' | 'address'> & Partial<FinanceCompany>): Promise<FinanceCompany> => {
    const response = await api.post('/finance/companies', data);
    return response.data;
  },
  update: async (id: string, data: Partial<FinanceCompany>): Promise<FinanceCompany> => {
    const response = await api.put(`/finance/companies/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/finance/companies/${id}`);
  }
};

// Finance (Delovodnik) - Book Types API
export const financeBookTypesAPI = {
  // NOTE: "Tipovi registratora" in production corresponds to finance document types.
  // Keep the public API name for UI route consistency, but point to `/finance/document-types`.
  getAll: async (): Promise<FinanceDocumentType[]> => {
    const response = await api.get('/finance/document-types');
    return response.data;
  },
  getById: async (id: string): Promise<FinanceDocumentType> => {
    const response = await api.get(`/finance/document-types/${id}`);
    return response.data;
  },
  create: async (data: Pick<FinanceDocumentType, 'name'> & Partial<FinanceDocumentType>): Promise<FinanceDocumentType> => {
    const response = await api.post('/finance/document-types', data);
    return response.data;
  },
  update: async (id: string, data: Partial<FinanceDocumentType>): Promise<FinanceDocumentType> => {
    const response = await api.put(`/finance/document-types/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/finance/document-types/${id}`);
  },
};

// Activity Logs API
export const activityLogsAPI = {
  getAll: async (params: {
    page?: number;
    limit?: number;
    userId?: string;
    entityType?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<{ activities: ActivityLog[]; pagination: { current: number; pages: number; total: number } }> => {
    // Admin route for paginated activity logs
    const response = await api.get('/admin/activity-logs', { params });
    return response.data;
  },
  exportCSV: async (params: {
    userId?: string;
    entityType?: string;
    action?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<Blob> => {
    // Dedicated export endpoint on activity-log router
    const response = await api.get('/activity-log/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },
  deleteAll: async (): Promise<{ message: string; deletedCount: number }> => {
    // Delete-all endpoint on activity-log router
    const response = await api.delete('/activity-log/delete-all');
    return response.data;
  },
};

// Chat API
export const chatAPI = {
  getRooms: async (): Promise<ChatRoom[]> => {
    const response = await api.get('/chat/rooms');
    return response.data;
  },
  createRoom: async (data: {
    participantIds: string[];
    name?: string;
    type?: 'direct' | 'group';
  }): Promise<ChatRoom> => {
    const response = await api.post('/chat/rooms', data);
    return response.data;
  },
  getMessages: async (roomId: string, params?: { limit?: number; before?: string }): Promise<ChatMessage[]> => {
    const response = await api.get(`/chat/rooms/${roomId}/messages`, { params });
    return response.data;
  },
  markAsRead: async (roomId: string): Promise<void> => {
    await api.post(`/chat/rooms/${roomId}/read`);
  },
  getUsers: async (): Promise<User[]> => {
    const response = await api.get('/chat/users');
    return response.data;
  },
  getUnreadCount: async (): Promise<{ unreadCount: number }> => {
    const response = await api.get('/chat/unread-count');
    return response.data;
  },
  getUserStatuses: async (): Promise<Record<string, UserStatus>> => {
    const response = await api.get('/chat/user-statuses');
    return response.data;
  },
  deleteRoom: async (roomId: string): Promise<{ message: string; roomId: string }> => {
    const response = await api.delete(`/chat/rooms/${roomId}`);
    return response.data;
  },
};

// Finance (Delovodnik) - Books API
export const financeBooksAPI = {
  getAll: async (params?: { bookTypeId?: string; companyId?: string; year?: number; isActive?: boolean }): Promise<FinanceBook[]> => {
    const response = await api.get('/finance/books', { params });
    return response.data;
  },
  getById: async (id: string): Promise<FinanceBook> => {
    const response = await api.get(`/finance/books/${id}`);
    return response.data;
  },
  create: async (data: { name: string; companyId: string; year?: number }): Promise<FinanceBook> => {
    const response = await api.post('/finance/books', data);
    return response.data;
  },
  update: async (id: string, data: Partial<Pick<FinanceBook, 'name' | 'year'>> & { companyId?: string }): Promise<FinanceBook> => {
    const response = await api.put(`/finance/books/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/finance/books/${id}`);
  }
};

// Finance (Delovodnik) - Documents API
export const financeDocumentsAPI = {
  getAll: async (params?: {
    bookId?: string;
    status?: string;
    startDate?: string;
    endDate?: string;
    documentType?: string;
    tags?: string;
  }): Promise<FinanceDocument[]> => {
    const response = await api.get('/finance/documents', { params });
    return response.data;
  },
  getById: async (id: string): Promise<FinanceDocument> => {
    const response = await api.get(`/finance/documents/${id}`);
    return response.data;
  },
  create: async (data: {
    bookId: string;
    baseNumber: string;
    subNumber?: string;
    dateReceived?: string; // ISO
    sender?: string;
    subject: string;
    documentType?: string;
    numberAndDate?: string;
    organizationalUnit?: string;
    oznaka?: string;
    description?: string;
  }): Promise<FinanceDocument> => {
    const response = await api.post('/finance/documents', data);
    return response.data;
  },
  update: async (id: string, data: Partial<{
    baseNumber: string;
    subNumber: string;
    dateReceived: string;
    sender: string;
    subject: string;
    description: string;
    numberAndDate: string;
    organizationalUnit: string;
    oznaka: string;
    documentType: string;
  }>): Promise<FinanceDocument> => {
    const response = await api.put(`/finance/documents/${id}`, data);
    return response.data;
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/finance/documents/${id}`);
  },
  uploadAttachments: async (documentId: string, files: File[]): Promise<{ message: string; files: Array<{ filename: string; originalName: string }> }> => {
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    const response = await api.post(`/finance/documents/${documentId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data;
  },
  getAttachmentUrl: (documentId: string, filename: string) => {
    // backend supports token via query param for iframe/img; we use it for direct download/preview links
    const token = localStorage.getItem('token');
    const encoded = encodeURIComponent(filename);
    const t = token ? `?token=${encodeURIComponent(token)}` : '';
    return `${API_BASE_URL}/finance/documents/${documentId}/attachments/${encoded}${t}`;
  }
};

// Task Notifications API
export interface TaskNotification {
  _id: string;
  type: string;
  message: string;
  taskId?: {
    _id: string;
    name: string;
    status?: string;
    priority?: string;
  };
  projectId?: {
    _id: string;
    name: string;
  };
  assignedBy: {
    _id: string;
    username: string;
    firstName: string;
    lastName: string;
  };
  isRead: boolean;
  readAt?: string;
  createdAt: string;
  updatedAt: string;
}

export const taskNotificationsAPI = {
  getAll: async (params?: { page?: number; limit?: number; unreadOnly?: boolean }): Promise<{
    notifications: TaskNotification[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> => {
    const response = await api.get('/task-notifications', { params });
    return response.data;
  },
  getUnreadCount: async (): Promise<{ unreadCount: number }> => {
    const response = await api.get('/task-notifications/unread-count');
    return response.data;
  },
  markAsRead: async (id: string): Promise<void> => {
    await api.post(`/task-notifications/mark-read/${id}`);
  },
  markAllAsRead: async (): Promise<void> => {
    await api.post('/task-notifications/mark-all-read');
  },
  delete: async (id: string): Promise<void> => {
    await api.delete(`/task-notifications/${id}`);
  },
  deleteAll: async (): Promise<void> => {
    await api.delete('/task-notifications');
  }
};

// Production Time API
export interface ProductionState {
  _id: string;
  name: string;
  code?: string;
  description?: string;
  color: string;
  icon: string;
  order: number;
  isPrimaryMode: boolean;
  deductsFromPrimary: boolean;
  isActive: boolean;
  createdBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    username: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface ProductionTimeEntry {
  _id: string;
  stateId: ProductionState | string;
  startTime: string;
  endTime?: string;
  duration?: number;
  date: string;
  notes?: string;
  createdBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    username: string;
  };
  updatedBy?: {
    _id: string;
    firstName: string;
    lastName: string;
    username: string;
  };
  createdAt: string;
  updatedAt: string;
  rawDuration?: number;
  deductedMinutes?: number;
  effectiveDuration?: number;
}

export interface ProductionTimeSummary {
  periodStart: string;
  periodEnd: string;
  totalMinutes: number;
  totalHours: number;
  regularMinutes: number;
  regularHours: number;
  totalDeductedMinutes: number;
  totalDeductedHours: number;
  deductionByState: Array<{
    stateId: string;
    stateName: string;
    stateColor: string;
    stateIcon?: string;
    totalMinutes: number;
    totalHours: number;
    entryCount: number;
  }>;
}

export const productionTimeAPI = {
  // States
  getStates: async (isActive?: boolean): Promise<ProductionState[]> => {
    const params = isActive !== undefined ? { isActive: isActive.toString() } : {};
    const response = await api.get('/production-time/states', { params });
    return response.data;
  },
  getStateById: async (id: string): Promise<ProductionState> => {
    const response = await api.get(`/production-time/states/${id}`);
    return response.data;
  },
  createState: async (stateData: Partial<ProductionState>): Promise<ProductionState> => {
    const response = await api.post('/production-time/states', stateData);
    return response.data;
  },
  updateState: async (id: string, stateData: Partial<ProductionState>): Promise<ProductionState> => {
    const response = await api.put(`/production-time/states/${id}`, stateData);
    return response.data;
  },
  deleteState: async (id: string): Promise<void> => {
    await api.delete(`/production-time/states/${id}`);
  },
  // Entries
  getEntries: async (params?: {
    startDate?: string;
    endDate?: string;
    periodStart?: string;
    periodEnd?: string;
    stateId?: string;
    isActive?: boolean;
  }): Promise<{
    entries: ProductionTimeEntry[];
    summary: ProductionTimeSummary;
  }> => {
    const response = await api.get('/production-time/entries', { params });
    return response.data;
  },
  getEntryById: async (id: string): Promise<ProductionTimeEntry> => {
    const response = await api.get(`/production-time/entries/${id}`);
    return response.data;
  },
  createEntry: async (entryData: {
    stateId: string;
    startTime: string;
    endTime?: string;
    notes?: string;
  }): Promise<ProductionTimeEntry> => {
    const response = await api.post('/production-time/entries', entryData);
    return response.data;
  },
  updateEntry: async (id: string, entryData: {
    stateId?: string;
    startTime?: string;
    endTime?: string;
    notes?: string;
  }): Promise<ProductionTimeEntry> => {
    const response = await api.put(`/production-time/entries/${id}`, entryData);
    return response.data;
  },
  deleteEntry: async (id: string): Promise<void> => {
    await api.delete(`/production-time/entries/${id}`);
  }
};

// Notifications API
export const notificationsAPI = {
  sendTestEmail: async (): Promise<{ message: string; email: string }> => {
    const response = await api.post('/notifications/test-email');
    return response.data;
  },
  getEmailConfig: async (): Promise<{
    enabled: boolean;
    host: string | null;
    port: string | null;
    user: string | null;
    from: string | null;
    secure: boolean;
    hasPassword: boolean;
    allConfigured: boolean;
  }> => {
    const response = await api.get('/notifications/email-config');
    return response.data;
  }
};
